import {
  faSignature,
  faCalendar,
  faPen,
  faCalendarPlus,
  faCalendarCheck,
  faHandWave,
  faVideo,
  faThumbsUp,
} from "@fortawesome/duotone-light-svg-icons"
import { faArrowRight } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Checkbox, Typography } from "@joonapp/web-shared"
import { JoonUIColor } from "@joonapp/web-shared"
import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useCoachingGroupsQuery } from "../../networking/coachingGroups/queries"
import { markCoachTodoAsComplete } from "../../networking/coachTodos/api"
import { useCoachTodosQuery } from "../../networking/coachTodos/queries"
import { ClientTab } from "../../pages/clients/ClientTabNav"
import { useQuestModalStore } from "../../pages/clients/routines/useQuestModalStore"
import useSessionModalStore from "../../pages/clients/sessions/hooks/useSessionModalStore"
import { queryClient } from "../../queryClient"
import { CoachTodo, CoachTodoType } from "../../types/coachTodos"
import { QueryKey } from "../../types/queries"

const TodoItem = ({ todo }: { todo: CoachTodo }) => {
  const { refetch } = useCoachTodosQuery()
  const todoContent = getTodoContent(todo.type)
  const todoAction = useTodoActions(todo)
  const [isCompleting, setIsCompleting] = useState(false)

  const handleCompleteTodo = async () => {
    if (todo.completed) return
    setIsCompleting(true)
    try {
      await markCoachTodoAsComplete({ todoId: todo.id })
      await refetch()
    } catch (error) {
      console.error(error)
    } finally {
      setIsCompleting(false)
    }
  }

  return (
    <div className="flex flex-row items-center gap-1 px-2">
      {todoContent.showCheckbox && (
        <div className="max-w-4 min-w-4 flex items-center justify-center ml-2">
          <Checkbox
            name={todoContent.title}
            hideBorder
            selected={todo.completed || isCompleting}
            onChange={handleCompleteTodo}
            size="small"
            borderColor={
              todo.completed
                ? JoonUIColor.border.accent
                : JoonUIColor.border.default
            }
          />
        </div>
      )}
      <button onClick={todoAction} className="w-full">
        <div className="flex flex-row items-center gap-2 justify-between w-full hover:bg-neutral-100 rounded-md px-2 py-1">
          <div className="flex flex-row items-center gap-1">
            <FontAwesomeIcon
              icon={todoContent.icon}
              color={
                todoContent.showCheckbox
                  ? JoonUIColor.icon.accent
                  : JoonUIColor.semantic.warning
              }
            />
            <Typography
              variant="bodySmall"
              color={
                todoContent.showCheckbox
                  ? JoonUIColor.text.primary
                  : JoonUIColor.semantic.warning
              }
            >
              {todoContent.title}
            </Typography>
          </div>

          <FontAwesomeIcon
            icon={faArrowRight}
            color={JoonUIColor.icon.neutral}
          />
        </div>
      </button>
    </div>
  )
}

export default TodoItem

export const useTodoActions = (todo: CoachTodo) => {
  const { data: coachingGroups } = useCoachingGroupsQuery()
  const { onOpen: openSessionModal, setSessionMarkedAsCompleted } =
    useSessionModalStore()
  const { onOpen: openQuestModal } = useQuestModalStore()
  const coachingGroup = coachingGroups?.find((group) =>
    group.profiles.some((profile) => profile.user.id === todo.client.id)
  )
  const navigate = useNavigate()
  const openChat = useCallback(() => {
    if (!coachingGroup) return
    navigate(`/${coachingGroup.id}/messages`)
  }, [coachingGroup, navigate])

  const openEmail = () => {
    // should open email composer
    window.open(`mailto:${todo.client.email}`, "_blank")
  }

  const openSessionTab = () => {
    if (!coachingGroup) return
    navigate(`/${coachingGroup.id}/${ClientTab.SESSIONS}`)
  }

  const openSession = () => {
    if (!coachingGroup) return
    navigate(`/${coachingGroup.id}/${ClientTab.SESSIONS}/`)
    if (!todo.associated_session?.id) return
    setSessionMarkedAsCompleted(true)
    openSessionModal(todo.associated_session?.id)
  }

  const openWeeklyRoutineTab = () => {
    if (!coachingGroup) return
    navigate(`/${coachingGroup.id}/${ClientTab.ROUTINES}`)
  }

  const openQuestDetails = () => {
    if (!coachingGroup) return
    navigate(`/${coachingGroup.id}/${ClientTab.ROUTINES}/`)
    if (!todo.associated_quest) return
    openQuestModal([todo.associated_quest])
    markCoachTodoAsComplete({ todoId: todo.id })
    queryClient.invalidateQueries({ queryKey: [QueryKey.COACH_TODOS] })
  }

  switch (todo.type) {
    case CoachTodoType.SIGN_TOS:
      return openEmail
    case CoachTodoType.FIRST_SESSION:
    case CoachTodoType.VIDEO_GREETING:
    case CoachTodoType.REENGAGED_MESSAGE:
    case CoachTodoType.TEXT_CHECKIN:
      return openChat
    case CoachTodoType.DOCUMENT_SESSION:
      return openSession
    case CoachTodoType.NEXT_SESSION:
      return openSessionTab
    case CoachTodoType.WEEKLY_ROUTINE:
      return openWeeklyRoutineTab
    case CoachTodoType.QUEST_COMPLETION:
      return openQuestDetails
  }
}

export const getTodoContent = (type: CoachTodoType) => {
  switch (type) {
    case CoachTodoType.SIGN_TOS:
      return {
        title: "Email reminder to sign TOS",
        icon: faSignature,
        showCheckbox: true,
      }
    case CoachTodoType.FIRST_SESSION:
      return {
        title: "Remind to schedule session",
        icon: faCalendar,
        showCheckbox: true,
      }
    case CoachTodoType.VIDEO_GREETING:
      return {
        title: "Send video greeting",
        icon: faVideo,
        showCheckbox: true,
      }
    case CoachTodoType.REENGAGED_MESSAGE:
      return {
        title: "Send welcome back message",
        icon: faHandWave,
        showCheckbox: false,
      }
    case CoachTodoType.DOCUMENT_SESSION:
      return { title: "Send session summary", icon: faPen, showCheckbox: false }
    case CoachTodoType.NEXT_SESSION:
      return {
        title: "Book next session",
        icon: faCalendarPlus,
        showCheckbox: false,
      }
    case CoachTodoType.WEEKLY_ROUTINE:
      return {
        title: "Create weekly routine",
        icon: faCalendarCheck,
        showCheckbox: false,
      }
    case CoachTodoType.TEXT_CHECKIN:
      return {
        title: "Time to check in!",
        icon: faHandWave,
        showCheckbox: false,
      }
    case CoachTodoType.QUEST_COMPLETION:
      return {
        title: "Parent task is ready to review",
        icon: faThumbsUp,
        showCheckbox: false,
      }
  }
}
